import { isObject } from './misc.js';

const { assetsCdnUrl } = require('../../../client-config.js');

const theme = {
  countdown: {
    liveTomorrowLabel: 'Tomorrow',
    startedTodayAtLabel: 'Started today at',
    titleCountdownLabel: { goingLive: 'Going live', goingLiveIn: 'Going live in', goingLiveOn: 'Going live on' },
    titleCountdownOverdueLabel: 'Starting soon',
    titleLabel: 'Starting soon',
    todayLabel: 'Today',
    tomorrowLabel: 'Tomorrow',
    yesterdayLabel: 'Yesterday',
  },
  channels: {
    addToCalendar: 'Add to calendar',
    copyShareLink: 'Copy link',
    endedSectionHeader: 'Watch again',
    endedShowStatusLabel: 'Watch again',
    highlightSectionHeader: 'Discover live shopping',
    otherCalendarLabel: 'Other',
    joinLiveButtonLabel: 'Join now',
    liveNowLabel: 'Live now',
    loadMoreButtonLabel: 'Load more',
    moreDetailsComingSoon: 'More details coming soon',
    readMoreLabel: 'Read more',
    share: 'Share',
    showMoreButtonLabel: 'Show more',
    upcomingSectionHeader: 'Calendar',
    upcomingShowStatusLabel: 'Next up',
    watchNowButtonLabel: 'Watch now',
  },
  statusLabels: {
    live: 'Live',
  },
  floatingWidget: {
    continueWatchingLabel: '',
  },
};

function applyLegacyTheme(newTheme) {
  if (theme.fonts.length === 0) {
    theme.fonts = [
      {
        family: '"Haas Grot"',
        style: 'normal',
        weight: 'normal',
        urls: [
          {
            format: 'woff2',
            url: `${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotText-55Roman-Web.woff2`,
          },
          {
            format: 'woff',
            url: `${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotText-55Roman-Web.woff`,
          },
        ],
      },
      {
        family: '"Haas Grot Display"',
        style: 'normal',
        weight: 'normal',
        urls: [
          {
            format: 'woff2',
            url: `${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotDisp-65Medium-Web.woff2`,
          },
          {
            format: 'woff',
            url: `${assetsCdnUrl}/fonts/neue-haas-grotesk/NeueHaasGrotDisp-65Medium-Web.woff`,
          },
        ],
      },
    ];
  }

  if (!theme.styles.base) {
    theme.styles.base = {
      fontFamily: '"Haas Grot", Helvetica, Arial, Verdana, Tahoma, sans-serif',
    };
  }

  if (!theme.styles.headings) {
    theme.styles.headings = {
      fontFamily: '"Haas Grot Display", Helvetica, Arial, Verdana, Tahoma, sans-serif',
    };
  }

  let composedStyles = '';

  const atImportFonts = theme.fonts.filter((font) => font.atImport);
  composedStyles += atImportFonts
    .map((font) => {
      if (font.atImport) {
        return `
          @import url('${font.atImport}');`;
      }
    })
    .join('');

  const fontFaceFonts = theme.fonts.filter((font) => font.urls);
  composedStyles += fontFaceFonts
    .map(
      (font) => `
        @font-face {
          font-family: ${font.family};
          src: ${font.urls.map((url) => `url("${url.url}") format("${url.format}")`).join(', ')};
          font-weight: ${font.weight || 'normal'};
          font-style: ${font.style || 'normal'};
          font-display: swap;
        }`,
    )
    .join('');

  const { base, headings, button, curtain, label, dropdown } = theme.styles;
  composedStyles += `
    html, body, button {
      font-family: ${base.fontFamily};
    }`;

  let rootStyles = '';
  if (base.bgColor) {
    rootStyles += `  --bg-color: ${base.bgColor};\n`;
  }
  if (base.fontFamily) {
    rootStyles += ` --font-family: ${base.fontFamily};\n`;
  }
  if (base.textColor) {
    rootStyles += `  --text-color: ${base.textColor};\n`;
  }
  if (base.textColorSecondary) {
    rootStyles += `  --text-secondary-color: ${base.textColorSecondary};\n`;
  }
  if (base.fontSizeSecondary) {
    rootStyles += `  --text-secondary-font-size: ${base.fontSizeSecondary};\n`;
  }
  if (base.borderRadius) {
    rootStyles += `  --border-radius: ${base.borderRadius};\n`;
  }
  if (button && button.primary && button.primary.textColor) {
    rootStyles += `  --button-text-color: ${button.primary.textColor};\n`;
  }
  if (button && button.primary && button.primary.borderColor) {
    rootStyles += `  --button-border-color: ${button.primary.borderColor};\n`;
  }
  if (button && button.primary && button.primary.fontWeight) {
    rootStyles += `  --button-font-weight: ${button.primary.fontWeight};\n`;
  }
  if (button && button.primary && button.primary.textTransform) {
    rootStyles += `  --button-text-transform: ${button.primary.textTransform};\n`;
  }
  if (button && button.primary && button.primary.bgColor) {
    rootStyles += `  --button-bg-color: ${button.primary.bgColor};\n`;
  }
  if (button && button.primary && button.primary.radius) {
    rootStyles += `  --button-radius: ${button.primary.radius}px;\n`;
  }
  if (button && button.secondary && button.secondary.bgColor) {
    rootStyles += `  --button-secondary-bg-color: ${button.secondary.bgColor};\n`;
  }
  if (button && button.secondary && button.secondary.textColor) {
    rootStyles += `  --button-secondary-text-color: ${button.secondary.textColor};\n`;
  }
  if (button && button.secondary && button.secondary.borderColor) {
    rootStyles += `  --button-secondary-border-color: ${button.secondary.borderColor};\n`;
  }
  if (button && button.secondary && button.secondary.radius) {
    rootStyles += `  --button-secondary-radius: ${button.secondary.radius};\n`;
  }
  if (button && button.secondary && button.secondary.textTransform) {
    rootStyles += `  --button-secondary-text-transform: ${button.secondary.textTransform};\n`;
  }
  if (button && button.secondary && button.secondary.fontWeight) {
    rootStyles += `  --button-secondary-font-weight: ${button.secondary.fontWeight};\n`;
  }
  if (curtain && curtain.pending && curtain.pending.textTransform) {
    rootStyles += `  --graphic-title-text-transform: ${curtain.pending.textTransform};\n`;
  }
  if (curtain && curtain.pending && curtain.pending.bgColor) {
    rootStyles += `  --graphic-bg-color: ${curtain.pending.bgColor};\n`;
  }
  if (curtain && curtain.pending && curtain.pending.textColor) {
    rootStyles += `  --graphic-text-color: ${curtain.pending.textColor};\n`;
    rootStyles += `  --graphic-border-color: ${curtain.pending.textColor};\n`;
  }
  if (headings && headings.fontFamily) {
    rootStyles += ` --headings-font-family: ${headings.fontFamily};\n`;
  }
  if (headings && headings.h1 && headings.h1.fontWeight) {
    rootStyles += ` --h1-font-weight: ${headings.h1.fontWeight};\n`;
  }
  if (headings && headings.h1 && headings.h1.fontSize) {
    rootStyles += ` --h1-font-size: ${headings.h1.fontSize};\n`;
  }
  if (headings && headings.mobile && headings.mobile.h1 && headings.mobile.h1.fontSize) {
    rootStyles += ` --h1-mobile-font-size: ${headings.mobile.h1.fontSize};\n`;
  }
  if (headings && headings.h1 && headings.h1.fontColor) {
    rootStyles += ` --h1-font-color: ${headings.h1.fontColor};\n`;
  }
  if (headings && headings.h1 && headings.h1.textTransform) {
    rootStyles += ` --h1-text-transform: ${headings.h1.textTransform};\n`;
  }
  if (headings && headings.h2 && headings.h2.fontWeight) {
    rootStyles += ` --h2-font-weight: ${headings.h2.fontWeight};\n`;
  }
  if (headings && headings.h2 && headings.h2.fontSize) {
    rootStyles += ` --h2-font-size: ${headings.h2.fontSize};\n`;
  }
  if (headings && headings.mobile && headings.mobile.h2 && headings.mobile.h2.fontSize) {
    rootStyles += ` --h2-mobile-font-size: ${headings.mobile.h2.fontSize};\n`;
  }
  if (headings && headings.h2 && headings.h2.fontColor) {
    rootStyles += ` --h2-font-color: ${headings.h2.fontColor};\n`;
  }
  if (headings && headings.h2 && headings.h2.textTransform) {
    rootStyles += ` --h2-text-transform: ${headings.h2.textTransform};\n`;
  }
  if (label && label.live && label.live.textColor) {
    rootStyles += `  --live-label-text-color: ${label.live.textColor};\n`;
  }
  if (label && label.live && label.live.fontSize) {
    rootStyles += `  --live-label-font-size: ${label.live.fontSize};\n`;
  }
  if (label && label.live && label.live.backgroundColor) {
    rootStyles += `  --live-label-bg-color: ${label.live.backgroundColor};\n`;
  }
  if (label && label.live && label.live.textTransform) {
    rootStyles += `  --live-label-text-transform: ${label.live.textTransform};\n`;
  }
  if (label && label.live && label.live.fontWeight) {
    rootStyles += `  --live-label-font-weight: ${label.live.fontWeight};\n`;
  }
  if (dropdown && dropdown.menu && dropdown.menu.fontSize) {
    rootStyles += `  --select-menu-font-size: ${dropdown.menu.fontSize};\n`;
  }
  if (dropdown && dropdown.menu && dropdown.menu.fontWeight) {
    rootStyles += `  --select-menu-font-weight: ${dropdown.menu.fontWeight};\n`;
  }
  if (dropdown && dropdown.menu && dropdown.menu.backgroundColor) {
    rootStyles += `  --select-menu-bg-color: ${dropdown.menu.backgroundColor};\n`;
  }
  if (dropdown && dropdown.menu && dropdown.menu.borderRadius) {
    rootStyles += `  --select-menu-border-radius: ${dropdown.menu.borderRadius};\n`;
  }
  if (dropdown && dropdown.menu && dropdown.menu.color) {
    rootStyles += `  --select-menu-label-text-color: ${dropdown.menu.color};\n`;
  }

  if (rootStyles.length > 0) {
    composedStyles += `
      :root {
        ${rootStyles}
      }`;
  }

  if (newTheme.customCss) {
    composedStyles += newTheme.customCss;
  }

  return composedStyles;
}

const fontFormat = (str = '') => {
  const extension = str.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'otc':
    case 'ttc':
      return 'collection';
    case 'eot':
      return 'embedded-opentype';
    case 'otf':
      return 'opentype';
    case 'ttf':
      return 'truetype';
    case 'svg':
    case 'svgz':
      return 'svg';
    case 'woff':
    case 'woff2':
      break;
    default:
      return '';
  }
  return extension;
};

const getFontSrc = (storageUrl, format) => {
  format = fontFormat(format);
  const formatStr = format ? `format('${format}')` : '';
  return `url('${storageUrl}') ${formatStr}`;
};

const manageWidgetFonts = (newTheme) => {
  if (!newTheme) return;

  const manageFontsV1 = newTheme?.styles?.general?.fonts?.fonts?.manageFonts || [];
  const manageFontsV2 = newTheme?.styles?.generalV2?.fonts?.fonts?.manageFonts || [];

  const manageFonts = [...manageFontsV1, ...manageFontsV2];

  if (!manageFonts.length) return;

  return manageFonts.map(
    ({ weight, style: fontStyle, name, storageUrl, format }) => `
          @font-face {
            font-family: '${name}';
            src: ${getFontSrc(storageUrl, format)};
            font-weight: ${weight};
            font-style: ${fontStyle};
            font-display: swap;
          }`,
  ).join('');
};

function applyChannelsThemingAPI(newTheme) {
  const { buttons, curtains, selects, page, statusBar } = newTheme.styles.channels;

  let composedStyles = '';
  let rootStyles = '';
  const manageFonts = manageWidgetFonts(newTheme);

  if (manageFonts) {
    composedStyles += manageFonts;
  }

  if (page && page.main && page.main.backgroundColor) {
    rootStyles += `  --bg-color: ${page.main.backgroundColor};\n`;
  }
  if (page && page.main && page.main.fontFamily) {
    rootStyles += ` --font-family: '${page.main.fontFamily}';\n`;
  }
  if (page && page.main && page.main.primaryText && page.main.primaryText.color) {
    rootStyles += `  --text-color: ${page.main.primaryText.color};\n`;
  }
  if (page && page.main && page.main.secondaryText && page.main.secondaryText.color) {
    rootStyles += `  --text-secondary-color: ${page.main.secondaryText.color};\n`;
  }
  if (page && page.main && page.main.secondaryText && page.main.secondaryText.fontSize) {
    rootStyles += `  --text-secondary-font-size: ${page.main.secondaryText.fontSize};\n`;
  }
  if (page && page.main && page.main.borderRadius) {
    rootStyles += `  --border-radius: ${page.main.borderRadius};\n`;
  }
  if (buttons && buttons.main && buttons.main.fontWeight) {
    rootStyles += `  --button-font-weight: ${buttons.main.fontWeight};\n`;
  }
  if (buttons && buttons.main && buttons.main.fontSize) {
    rootStyles += `  --button-font-size: ${buttons.main.fontSize};\n`;
  }
  if (buttons && buttons.main && buttons.main.textTransform) {
    rootStyles += `  --button-text-transform: ${buttons.main.textTransform};\n`;
  }
  if (buttons && buttons.main && buttons.main.borderRadius) {
    rootStyles += `  --button-radius: ${buttons.main.borderRadius};\n`;
  }
  if (buttons && buttons.primary && buttons.primary.color) {
    rootStyles += `  --button-text-color: ${buttons.primary.color};\n`;
  }
  if (buttons && buttons.primary && buttons.primary.borderColor) {
    rootStyles += `  --button-border-color: ${buttons.primary.borderColor};\n`;
  }
  if (buttons && buttons.primary && buttons.primary.backgroundColor) {
    rootStyles += `  --button-bg-color: ${buttons.primary.backgroundColor};\n`;
  }
  if (buttons && buttons.secondary && buttons.secondary.backgroundColor) {
    rootStyles += `  --button-secondary-bg-color: ${buttons.secondary.backgroundColor};\n`;
  }
  if (buttons && buttons.secondary && buttons.secondary.color) {
    rootStyles += `  --button-secondary-text-color: ${buttons.secondary.color};\n`;
  }
  if (buttons && buttons.secondary && buttons.secondary.borderColor) {
    rootStyles += `  --button-secondary-border-color: ${buttons.secondary.borderColor};\n`;
  }
  if (curtains && curtains.curtainPending && curtains.curtainPending.textTransform) {
    rootStyles += `  --graphic-title-text-transform: ${curtains.curtainPending.textTransform};\n`;
  }
  if (curtains && curtains.curtainPending && curtains.curtainPending.backgroundColor) {
    rootStyles += `  --graphic-bg-color: ${curtains.curtainPending.backgroundColor};\n`;
  }
  if (curtains && curtains.curtainPending && curtains.curtainPending.color) {
    rootStyles += `  --graphic-text-color: ${curtains.curtainPending.color};\n`;
    rootStyles += `  --graphic-border-color: ${curtains.curtainPending.color};\n`;
  }
  if (page && page.headings && page.headings.fontFamily) {
    rootStyles += ` --headings-font-family: '${page.headings.fontFamily}';\n`;
  }
  if (page && page.headings && page.headings.h1Headings && page.headings.h1Headings.fontWeight) {
    rootStyles += ` --h1-font-weight: ${page.headings.h1Headings.fontWeight};\n`;
  }
  if (page && page.headings && page.headings.h1Headings && page.headings.h1Headings.fontSize) {
    rootStyles += ` --h1-font-size: ${page.headings.h1Headings.fontSize};\n`;
  }
  if (
    page &&
    page.headings &&
    page.headings.h1HeadingsMobile &&
    page.headings.h1HeadingsMobile &&
    page.headings.h1HeadingsMobile.fontSize
  ) {
    rootStyles += ` --h1-mobile-font-size: ${page.headings.h1HeadingsMobile.fontSize};\n`;
  }
  if (page && page.headings && page.headings.h1Headings && page.headings.h1Headings.color) {
    rootStyles += ` --h1-font-color: ${page.headings.h1Headings.color};\n`;
  }
  if (page && page.headings && page.headings.h1Headings && page.headings.h1Headings.textTransform) {
    rootStyles += ` --h1-text-transform: ${page.headings.h1Headings.textTransform};\n`;
  }
  if (page && page.headings && page.headings.h2Headings && page.headings.h2Headings.fontWeight) {
    rootStyles += ` --h2-font-weight: ${page.headings.h2Headings.fontWeight};\n`;
  }
  if (page && page.headings && page.headings.h2Headings && page.headings.h2Headings.fontSize) {
    rootStyles += ` --h2-font-size: ${page.headings.h2Headings.fontSize};\n`;
  }
  if (
    page &&
    page.headings &&
    page.headings.h2HeadingsMobile &&
    page.headings.h2HeadingsMobile &&
    page.headings.h2HeadingsMobile.fontSize
  ) {
    rootStyles += ` --h2-mobile-font-size: ${page.headings.h2HeadingsMobile.fontSize};\n`;
  }
  if (page && page.headings && page.headings.h2Headings && page.headings.h2Headings.color) {
    rootStyles += ` --h2-font-color: ${page.headings.h2Headings.color};\n`;
  }
  if (page && page.headings && page.headings.h2Headings && page.headings.h2Headings.textTransform) {
    rootStyles += ` --h2-text-transform: ${page.headings.h2Headings.textTransform};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.fontFamily) {
    rootStyles += `  --live-label-font-family: '${statusBar.statusLive.fontFamily}';\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.color) {
    rootStyles += `  --live-label-text-color: ${statusBar.statusLive.color};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.fontSize) {
    rootStyles += `  --live-label-font-size: ${statusBar.statusLive.fontSize};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.backgroundColor) {
    rootStyles += `  --live-label-bg-color: ${statusBar.statusLive.backgroundColor};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.textTransform) {
    rootStyles += `  --live-label-text-transform: ${statusBar.statusLive.textTransform};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.fontWeight) {
    rootStyles += `  --live-label-font-weight: ${statusBar.statusLive.fontWeight};\n`;
  }
  if (selects && selects.menu && selects.menu.label && selects.menu.label.fontSize) {
    rootStyles += `  --select-menu-font-size: ${selects.menu.label.fontSize};\n`;
  }
  if (selects && selects.menu && selects.menu.label && selects.menu.label.fontWeight) {
    rootStyles += `  --select-menu-font-weight: ${selects.menu.label.fontWeight};\n`;
  }
  if (selects && selects.menu && selects.menu.backgroundColor) {
    rootStyles += `  --select-menu-bg-color: ${selects.menu.backgroundColor};\n`;
  }
  if (selects && selects.menu && selects.menu.borderRadius) {
    rootStyles += `  --select-menu-border-radius: ${selects.menu.borderRadius};\n`;
  }
  if (selects && selects.menu && selects.menu.label && selects.menu.label.color) {
    rootStyles += `  --select-menu-label-text-color: ${selects.menu.label.color};\n`;
  }

  if (rootStyles.length > 0) {
    composedStyles += `
      :root {
        ${rootStyles}
      }`;
  }

  return composedStyles;
}

function applyFABThemingAPI(newTheme) {
  const { curtains, main, statusBar } = newTheme.styles.floatingActionButton;

  let composedStyles = '';
  let rootStyles = '';
  const manageFonts = manageWidgetFonts(newTheme);

  if (manageFonts) {
    composedStyles += manageFonts;
  }

  if (main && main.button && main.button.borderRadius) {
    rootStyles += `  --border-radius: ${main.button.borderRadius};\n`;
  }
  if (main && main.button && main.button.fontFamily) {
    rootStyles += ` --font-family: ${main.button.fontFamily};\n`;
  }
  if (curtains && curtains.curtainPending && curtains.curtainPending.backgroundColor) {
    rootStyles += `  --graphic-bg-color: ${curtains.curtainPending.backgroundColor};\n`;
  }
  if (curtains && curtains.curtainPending && curtains.curtainPending.color) {
    rootStyles += `  --graphic-text-color: ${curtains.curtainPending.color};\n`;
    rootStyles += `  --graphic-border-color: ${curtains.curtainPending.color};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.fontFamily) {
    rootStyles += `  --live-label-font-family: ${statusBar.statusLive.fontFamily};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.color) {
    rootStyles += `  --live-label-text-color: ${statusBar.statusLive.color};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.fontSize) {
    rootStyles += `  --live-label-font-size: ${statusBar.statusLive.fontSize};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.backgroundColor) {
    rootStyles += `  --live-label-bg-color: ${statusBar.statusLive.backgroundColor};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.textTransform) {
    rootStyles += `  --live-label-text-transform: ${statusBar.statusLive.textTransform};\n`;
  }
  if (statusBar && statusBar.statusLive && statusBar.statusLive.fontWeight) {
    rootStyles += `  --live-label-font-weight: ${statusBar.statusLive.fontWeight};\n`;
  }

  if (rootStyles.length > 0) {
    composedStyles += `
      :root {
        ${rootStyles}
      }`;
  }

  return composedStyles;
}

// Apply generalV2 theme
function applyThemingAPI(newTheme) {
  const { buttons, colors, text, curtain, playerSettings } = newTheme.styles.generalV2;

  let composedStyles = '';
  let rootStyles = '';

  const manageFonts = manageWidgetFonts(newTheme);

  if (manageFonts) {
    composedStyles += manageFonts;
  }

  // ========================== Begin Base
  if (text?.default?.fontFamily) {
    rootStyles += ` --font-family: ${text?.default?.fontFamily};\n`;
  }

  if (playerSettings?.misc?.borderRadiusBase) {
    // Directly reuse the borderRadiusBase from generalV2 for now
    rootStyles += `  --border-radius: ${playerSettings?.misc?.borderRadiusBase};\n`;
  }

  if (colors?.main?.['color-inverted']) { // channels-specific
    rootStyles += `  --text-color: ${colors?.main?.['color-inverted']};\n`;
  }

  if (colors?.main?.['color-inverted-subdued']) { // channels-specific
    rootStyles += `  --text-secondary-color: ${colors?.main?.['color-inverted-subdued']};\n`;
  }

  const body20 = text?.['body-20'];
  if (isObject(body20) && Object.keys(body20).length > 0) {
    const {
      fontSize,
    } = body20;

    rootStyles += `  --text-secondary-font-size: ${fontSize};\n`;
  }

  if (colors?.other?.['color-base']) { // channels-specific
    rootStyles += `  --bg-color: ${colors?.other?.['color-base']};\n`;
  }
  // ========================== End Base

  // ========================== Begin Graphic
  if (curtain?.colors?.backgroundColor) {
    rootStyles += `  --graphic-bg-color: ${curtain.colors.backgroundColor};\n`;
  }

  if (curtain?.colors?.foregroundColor) {
    rootStyles += `  --graphic-text-color: ${curtain.colors.foregroundColor};\n`;
    rootStyles += `  --graphic-border-color: ${curtain.colors.foregroundColor};\n`;
  }

  // Unmapped for curtain - channels only:
  // --graphic-title-text-transform (countdown on the featured show)
  // ========================== End Graphic

  // ========================== Begin Buttons (in channels)
  const label20 = text?.['label-20'];
  if (isObject(label20) && Object.keys(label20).length > 0) {
    const {
      fontSize,
      fontWeight,
      textTransform,
    } = label20;

    rootStyles += `  --button-font-size: ${fontSize};\n`;
    rootStyles += `  --button-font-weight: ${fontWeight};\n`;
    rootStyles += `  --button-secondary-font-weight: ${fontWeight};\n`;
    rootStyles += `  --button-text-transform: ${textTransform};\n`;
    rootStyles += `  --button-secondary-text-transform: ${textTransform};\n`;
  }

  // Primary buttons
  if (buttons?.['button-cta-enabled']?.foregroundColor) {
    rootStyles += ` --button-text-color: ${buttons['button-cta-enabled'].foregroundColor};\n`;
  }

  if (buttons?.['button-cta-enabled']?.borderColor) {
    rootStyles += ` --button-border-color: ${buttons['button-cta-enabled'].borderColor};\n`;
  }

  if (buttons?.['button-cta-enabled']?.backgroundColor) {
    rootStyles += ` --button-bg-color: ${buttons['button-cta-enabled'].backgroundColor};\n`;
  }

  if (buttons?.['button-cta-enabled']?.borderRadius) {
    rootStyles += ` --button-radius: ${buttons['button-cta-enabled'].borderRadius};\n`;
  }

  // Secondary buttons
  if (buttons?.['button-secondary-cta-enabled']?.foregroundColor) {
    rootStyles += ` --button-secondary-text-color: ${buttons['button-secondary-cta-enabled'].foregroundColor};\n`;
  }

  if (buttons?.['button-secondary-cta-enabled']?.borderColor) {
    rootStyles += ` --button-secondary-border-color: ${buttons['button-secondary-cta-enabled'].borderColor};\n`;
  }

  if (buttons?.['button-secondary-cta-enabled']?.backgroundColor) {
    rootStyles += ` --button-secondary-bg-color: ${buttons['button-secondary-cta-enabled'].backgroundColor};\n`;
  }

  if (buttons?.['button-secondary-cta-enabled']?.borderRadius) {
    rootStyles += ` --button-secondary-radius: ${buttons['button-secondary-cta-enabled'].borderRadius};\n`;
  }

  if (buttons?.['button-secondary-cta-disabled']?.foregroundColor) {
    rootStyles += ` --button-secondary-disabled-text-color: ${buttons['button-secondary-cta-disabled'].foregroundColor};\n`;
  }

  if (buttons?.['button-secondary-cta-disabled']?.borderColor) {
    rootStyles += ` --button-secondary-disabled-border-color: ${buttons['button-secondary-cta-disabled'].borderColor};\n`;
  }

  if (buttons?.['button-secondary-cta-disabled']?.backgroundColor) {
    rootStyles += ` --button-secondary-disabled-bg-color: ${buttons['button-secondary-cta-disabled'].backgroundColor};\n`;
  }
  // ========================== End Buttons

  // ========================== Begin Menu (in channels)
  if (colors?.main?.['color-inverted']) { // channels-specific
    rootStyles += `  --select-menu-label-text-color: ${colors?.main?.['color-inverted']};\n`;
  }

  if (playerSettings?.misc?.borderRadiusBase) {
    // Directly reuse the borderRadiusBase from generalV2 for now
    // Workaround for the missing borderRadius in the channels theme
    // May be a matter for the future adjustments
    rootStyles += `  --select-menu-border-radius: ${playerSettings?.misc?.borderRadiusBase};\n`;
  }

  if (colors?.other?.['color-base']) { // channels-specific
    rootStyles += `  --select-menu-bg-color: ${colors?.other?.['color-base']};\n`;
  }

  const body10 = text?.['body-10'];

  if (isObject(body10) && Object.keys(body10).length > 0) {
    const {
      fontSize,
      fontWeight,
    } = body10;

    rootStyles += `  --select-menu-font-size: ${fontSize};\n`;
    rootStyles += `  --select-menu-font-weight: ${fontWeight};\n`;
  }

  // ========================== Begin Live label
  if (colors?.main?.['color-highlight']) {
    rootStyles += `  --live-label-bg-color: ${colors?.main?.['color-highlight']};\n`;
  }

  // Color base is not editable by users, it's always white
  // But we're explicitly setting it to the color-base
  if (colors?.other?.['color-base']) {
    rootStyles += `  --live-label-text-color: ${colors?.other?.['color-base']};\n`;
  }

  const label10 = text?.['label-10'];
  if (isObject(label10) && Object.keys(label10).length > 0) {
    // We are not using all the data from the label10 object
    const {
      fontFamily,
      fontSize,
      fontWeight,
    } = label10;

    rootStyles += `  --live-label-font-family: ${fontFamily};\n`;
    rootStyles += `  --live-label-font-size: ${fontSize};\n`;
    rootStyles += `  --live-label-font-weight: ${fontWeight};\n`;

    // The text transform is FAB is usually uppercase,
    // but in player it's not the default case.
    // Force-setting it to the uppercase.
    rootStyles += '  --live-label-text-transform: uppercase;\n';
  }
  // ========================== End Live label

  // ========================== Begin Headings (Typography) - channels only
  const heading10 = text?.['heading-10'];
  if (isObject(heading10) && Object.keys(heading10).length > 0) {
    const {
      fontFamily,
      fontSize,
      fontWeight,
      textTransform,
    } = heading10;

    // Area of workarounds
    rootStyles += `  --headings-font-family: ${fontFamily};\n`;

    rootStyles += `  --h1-font-size: calc(${fontSize} * 1.5);\n`; // 36px by default
    rootStyles += `  --h1-mobile-font-size: ${fontSize};\n`; // 24px by default
    rootStyles += `  --h1-font-weight: ${fontWeight};\n`;
    rootStyles += `  --h1-text-transform: ${textTransform};\n`;

    rootStyles += `  --h2-font-size: ${fontSize};\n`; // 24px by default
    rootStyles += `  --h2-mobile-font-size: calc(${fontSize} / 1.3);\n`; // 18px by default
    rootStyles += `  --h2-font-weight: ${fontWeight};\n`;
    rootStyles += `  --h2-text-transform: ${textTransform};\n`;
  }

  if (colors?.main?.['color-inverted']) {
    // In prod these values --h1-font-color and --h2-font-color are usually the same
    rootStyles += `  --h1-font-color: ${colors?.main?.['color-inverted']};\n`;
    rootStyles += `  --h2-font-color: ${colors?.main?.['color-inverted']};\n`;
  }
  // ========================== End Headings

  if (rootStyles.length > 0) {
    composedStyles += `
      :root {
        ${rootStyles}
      }`;
  }

  return composedStyles;
}

export function applyTheme(newTheme, widgetName) {
  newTheme = newTheme || {};
  theme.styles = newTheme.styles || {};
  theme.fonts = newTheme.fonts || [];

  if (newTheme.countdown) {
    theme.countdown = newTheme.countdown;
  }
  if (newTheme.channels) {
    theme.channels = newTheme.channels;
  }
  if (newTheme.statusLabels) {
    theme.statusLabels = newTheme.statusLabels;
  }
  if (newTheme.floatingWidget) {
    theme.floatingWidget = newTheme.floatingWidget;
  }

  let stylesElem = document.querySelector('style[id=theme]');
  if (!stylesElem) {
    stylesElem = document.createElement('style');
    stylesElem.setAttribute('type', 'text/css');
    stylesElem.setAttribute('id', 'theme');
    document.head.appendChild(stylesElem);
  }

  let composedStyles;
  const hasGeneralObject = newTheme.styles && newTheme.styles.general;
  const hasGeneralV2Object = newTheme.styles && newTheme.styles.generalV2;

  if (!newTheme.isLegacyTheme && hasGeneralObject) {
    if (newTheme.styles.channels) {
      composedStyles = applyChannelsThemingAPI(newTheme);
    } else if (newTheme.styles.floatingActionButton) {
      composedStyles = applyFABThemingAPI(newTheme);
    } else if (hasGeneralV2Object) {
      composedStyles = applyThemingAPI(newTheme);
    } else {
      composedStyles = applyLegacyTheme(newTheme);
    }
  } else {
    composedStyles = applyLegacyTheme(newTheme);
  }

  if ('textContent' in stylesElem) {
    stylesElem.textContent = composedStyles;
  } else {
    stylesElem.styleSheet.cssText = composedStyles;
  }

  return { composedStyles };
}

export default theme;
